import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useThunkDispatch from 'packages/hooks/useThunkDispatch';
import type { InitialUpload } from 'packages/pages/uploads/types';
import useElementCreator from 'packages/pages/editor/useElementCreator';
import useEditor from '~/hooks/useEditor';
import { getCreative } from 'packages/store/creatives/creatives.selectors';
import { saveCreativeTemplate } from 'packages/store/creativeEditor/creativeEditor.actions';

type LocationState = { initialUpload?: InitialUpload } | null;

const PREVIEW_RESOLUTION = '1920x1920';

/**
 * When navigated to the design editor with `state.initialUpload` set,
 * this component automatically creates an image element from that upload
 * on the first render (after the creative model is ready).
 *
 * It must be rendered inside EditorProvider so it can access the creative model
 * via useElementCreator.
 *
 * A freshly created upload's `fileUrl` is the raw S3 upload (PUT) URL, which an
 * `<img>` cannot load (it's signed for `content-type` and returns 403 on GET), so
 * it must never be used as an element `src`. The displayable URL is the processed
 * `1920x1920` thumbnail. The "Create design" action is gated until that thumbnail
 * exists, so the upload always arrives here with a ready preview to render.
 */
const InitialUploadPlacer = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useThunkDispatch();
    const elementCreator = useElementCreator();
    const { creativeModel, exportIntegrations, exportConfigurationIds, approvalEnabled, reviewers } = useEditor();
    const creative = useSelector(getCreative)!;
    const placed = useRef(false);

    const state = location.state as LocationState;
    const uploadRef = useRef(state?.initialUpload);
    const initialUpload = uploadRef.current;

    useEffect(() => {
        if (!initialUpload || placed.current) {
            return;
        }

        placed.current = true;

        // Place and save the image synchronously so the design is persisted right
        // away, even if the user navigates off immediately. The preview thumbnail
        // is guaranteed to exist because "Create design" is disabled until then.
        elementCreator.createImageElement({
            id: initialUpload.id,
            type: 'ASSET_MANAGER' as const,
            src: initialUpload.thumbnails[PREVIEW_RESOLUTION],
            name: initialUpload.name ?? initialUpload.fileName,
            width: initialUpload.width,
            height: initialUpload.height,
        });

        dispatch(
            saveCreativeTemplate(
                creative.id,
                creativeModel,
                exportIntegrations,
                exportConfigurationIds,
                () => {},
                approvalEnabled,
                reviewers,
                creative.type,
                true,
            ),
        );

        // Clear the navigation state to avoid re-placing the image if the user
        // navigates away and returns to this URL.
        navigate(location.pathname + location.search, { replace: true });
    }, []);

    return null;
};

export default InitialUploadPlacer;
